import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, map, Observable, retry} from 'rxjs';
import {Taxonomy, TaxonomyHeader} from '../models';
import { AuthService } from "./auth.service";
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private resource: string = `${environment.wallet_api_url}${environment.v1}/Inventory/ProductCategories`;  // api/v1

  globalCategoriesSubject: BehaviorSubject<Taxonomy[]> = new BehaviorSubject<Taxonomy[]>([]);

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
  ) { }


  getParentTaxonomies(lang: string): Observable<Taxonomy[]> {
    return this.httpClient.get<TaxonomiesResponse>(`${this.resource}`, {
      params: new HttpParams().set('locale', lang),
      headers: this.getHeader(),
    }).pipe(
      retry(environment.retry_on_fail),
      map((taxonomiesRes) => {
        const rootTaxonomies = taxonomiesRes.data?.categories.map(x => this.mapFromDto(x))
        this.globalCategoriesSubject.next(rootTaxonomies)
        return rootTaxonomies;
      })
    );
  }

  getFinalCategories(lang: string): TaxonomyHeader {
    let categories: TaxonomyHeader = {
      headers: [
        {
          text: "ALL_CATEGORIES"
        }
      ],
      links: []
    };
    this.getParentTaxonomies(lang).subscribe((data) => {
      data[0]?.children.forEach((element) => {
        categories.headers[0].text = data[0]?.name;
        categories.links = [
          ...categories.links,
          {
            id: element?.id,
            spreeId: element.spreeId,
            text: `${element?.name}`,
            route: `/category/${element?.id}`,
            links: element?.children?.length > 0 ? [...this.transformSubCategory(element?.children, element?.name)] : []
          },
        ]
      });
    });
    return categories;
  }

  transformSubCategory(sub_categories: any[], parent_name: string) {
    let result: any = [];
    sub_categories.forEach((sub_category: any, index: number) => {
      result = [
        ...result,
        {
          "id": sub_category?.id,
          "text": sub_category?.name,
          "route": `/category/${sub_category?.id}`,
          "headers": index == 0 ? [parent_name] : [],
          "links": sub_category?.children?.length > 0 ? [...this.transformSubCategory(sub_category?.children, sub_category?.name)] : []
        }
      ]
    });
    return result;
  }

  /**
   *
   * @param taxonomyId
   * @param lang
   */
  async getTaxonomyById(taxonomyId: string, lang: string): Promise<Taxonomy | null> {
    let taxonomy: Taxonomy | null = null;
    let allTaxonomies = this.globalCategoriesSubject.getValue();
    if (allTaxonomies.length === 0) {
      allTaxonomies = await new Promise((resolve) => {
        this.getParentTaxonomies(lang).subscribe((pt) => resolve(pt));
      });
    }

    for (let tax of allTaxonomies) {
      if (tax.id === taxonomyId) {
        taxonomy = tax;
      } else {
        taxonomy = this.getTaxonomyInChildrenById(taxonomyId, tax);
      }
      if (taxonomy !== null) {
        break;
      }
    }
    return taxonomy;
  }

  private getTaxonomyInChildrenById(taxonomyId: string, parent: Taxonomy): Taxonomy | null {
    let taxonomy: Taxonomy | null = null;
    if (taxonomyId === parent.id) {
      return parent;
    }
    if (parent.children.length === 0) {
      return taxonomy;
    }

    for (let tax of parent.children) {
      taxonomy = this.getTaxonomyInChildrenById(taxonomyId, tax);
      if (taxonomy !== null) {
        break;
      }
    }

    return taxonomy;
  }

  private mapFromDto(taxonomyDto: CategoryDto): Taxonomy {
    return {
      id: taxonomyDto.id,
      name: taxonomyDto.name,
      spreeId: taxonomyDto.spreeId,
      parentId: taxonomyDto.parent_id,
      depth: taxonomyDto.depth,
      permalink: taxonomyDto.permalink,
      children: taxonomyDto.children.map(x => this.mapFromDto(x))
    }
  }

  /**
   * Get headers
   * @private
   */
  private getHeader(): HttpHeaders {
    return this.authService.getHeaderNoRefresh();
  }
}

interface TaxonomiesResponse {
  fails: boolean
  data: {
    categories: CategoryDto[]
  }
}

interface CategoryDto {
  id: string
  parent_id: string | null
  name: string
  spreeId: string
  depth: number
  permalink: string
  children: CategoryDto[]
}
